import { useCallback, type FunctionComponent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { SourceType } from '@trello/analytics-types';
import { PopoverMenu, PopoverMenuButton } from '@trello/nachos/popover-menu';

import {
  GOOGLE_SERVICE_KEY,
  OUTLOOK_SERVICE_KEY,
} from '../../Planner.constants';
import { useAuthenticateCalendar } from '../../useAuthenticateCalendar';
import { useOnSuccessfulAuth } from '../../useOnSuccessfulAuth';
import { usePlannerAccounts } from '../../usePlannerAccounts';

import * as styles from './ConnectAccountPopoverScreen.module.less';

export const ConnectAccountPopoverScreen: FunctionComponent<{
  setIsAuthenticating?: (loading: boolean) => void;
  onAfterAuth?: () => void;
  analyticsSource?: SourceType;
}> = ({ setIsAuthenticating, onAfterAuth, analyticsSource }) => {
  const intl = useIntl();

  const { hasReachedAccountLimit } = usePlannerAccounts();
  const { onSuccessfulAuth } = useOnSuccessfulAuth();

  const handleSuccessfulAuth = useCallback(async () => {
    await onSuccessfulAuth();
    onAfterAuth?.();
  }, [onSuccessfulAuth, onAfterAuth]);

  const authenticateCalendar = useAuthenticateCalendar({
    setIsAuthenticating,
    onSuccessfulAuth: handleSuccessfulAuth,
    analyticsSource,
  });

  const googleCalendarButtonText = intl.formatMessage({
    id: 'templates.planner.google',
    defaultMessage: 'Google',
    description: 'Google calendar button',
  });

  const outlookCalendarButtonText = intl.formatMessage({
    id: 'templates.planner.outlook',
    defaultMessage: 'Outlook',
    description: 'Outlook calendar button',
  });

  if (hasReachedAccountLimit) {
    return (
      <FormattedMessage
        id="templates.planner.account-limit-reached"
        defaultMessage="You can only connect up to 10 accounts. Disconnect one to make room for a new account."
        description="Account limit reached message"
      />
    );
  }

  return (
    <PopoverMenu className={styles.popoverMenu}>
      <PopoverMenuButton
        title={googleCalendarButtonText}
        onClick={() => authenticateCalendar(GOOGLE_SERVICE_KEY)}
      />
      <PopoverMenuButton
        title={outlookCalendarButtonText}
        onClick={() => authenticateCalendar(OUTLOOK_SERVICE_KEY)}
      />
    </PopoverMenu>
  );
};
